<template>
  <tab-content
    title="CSV Settings"
    :before-change="submitConfig"
  >
    <DefineHierarchy
      :validation-for-level="validationObjectForLevel"
      :disabled="loading"
    />
    <DefineTotals
      :v="$v.form.fieldMap"
      :disabled="loading"
    />
  </tab-content>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import DefineHierarchy from '@/components/admin/upload/CSVSettings/DefineHierarchy/DefineHierarchy.vue'
import DefineTotals from '@/components/admin/upload/CSVSettings/DefineTotals/DefineTotals.vue'
import { FIELD_MAP_CONFIG_KEY, SHOW_MAP_CONFIG_KEY } from '@/constants'
import { postConfig } from '@/api'
import { useUploadStore } from '@/store/admin/upload'

const components = {
  DefineHierarchy,
  DefineTotals,
}

const props = {
  loading: {
    type: Boolean,
    default: false,
  },
}

@Component({ components })
export default class CSVSettings extends Vue.extend({ props }) {
  upload = useUploadStore()
  validations () {
    return {
      form: {
        levelFields: {
          areAllLevelsValid: () => this.areAllLevelsValid,
        },
        fieldMap: {
          atLeastOneTotalSelected: () => this.atLeastOneTotalSelected,
        },
      },
    }
  }

  get form () {
    return {
      fieldMap: this.upload.fieldMap,
      levelFields: this.upload.levelFields,
    }
  }

  get atLeastOneTotalSelected () {
    return this.upload.fieldMap.filter(f => f.total).length > 0
  }

  get showMap () {
    if (SHOW_MAP_CONFIG_KEY in this.$route.query) {
      return this.$route.query[SHOW_MAP_CONFIG_KEY] === 'true'
    } else {
      return true
    }
  }

  get areAllLevelsValid () {
    return this.upload.allLevels
      .map(level => this.isLevelValid(level))
      .every(valid => valid)
  }

  get isLevelValid () {
    return (level:number):boolean => Object.values(this.levelValidations(level)).every(v => v)
  }

  get levelValidations () {
    return (level:number):any => {
      const field = this.upload.levelFields[level]
      return {
        required: !!field,
        noBlanks: this.upload.noBlanks(level),
        valid: this.upload.validLevel(level),
        unique: level !== this.upload.levelCount || this.upload.uniqueLeafLevel,
      }
    }
  }

  get validationObjectForLevel () {
    return (level:number):any => ({
      $error: !this.isLevelValid(level),
      $dirty: this.$v.$dirty,
      validations: this.levelValidations(level),
    })
  }

  async submitConfig () {
    this.$v.$touch()
    if (!this.$v.$invalid) {
      // Add config from CSVSettings
      await postConfig({ key: FIELD_MAP_CONFIG_KEY, value: this.upload.fieldMap })
      // Fetch the data and populate Vuex
      await this.$typedStore.visualisationData.getTreeData()
      await this.$typedStore.primary.app.getConfig()
      await this.$typedStore.primary.entities.images.getAllImages()
      // Create the nodes for all paths
      await this.upload.createNodes()
      // Add default config to non-leaf nodes
      await this.upload.addConfigToNodes()
      // Fetch nodes and populate Vuex
      await this.$typedStore.primary.entities.nodes.getNodes()

      return true
    } else {
      return false // Form has validation errors
    }
  }
}
</script>
